/**
 * What the repertoire footer shows.
 *
 * A phone hides the header while a piece plays, and the footer slides away
 * with it. Pause has to stay reachable there, so on a phone (outside
 * performance mode) only a small Pause is left, in the bottom-right corner
 * below the score. Restart is not left: a reach for Pause, or a tap in the
 * corner, must not restart the piece.
 */
export type FooterTransportLayoutInput = {
  headerVisible: boolean;
  keepTransportVisible: boolean;
  playing: boolean;
  ended: boolean;
};

export type FooterTransportLayout = {
  /** The footer is on screen (the whole of it, or only Pause). */
  footerOnScreen: boolean;
  /** Only the transport is left; the rest of the footer has slid away. */
  transportOnly: boolean;
  showRestart: boolean;
  /** Pause is the small corner control. */
  compactPause: boolean;
};

export const COMPACT_PAUSE_SIZE_PX = 48;

const insetOffset = (side: 'right' | 'bottom', minimumPx: number) =>
  `max(${minimumPx}px, calc(env(safe-area-inset-${side}) + 8px))`;

export const TRANSPORT_OFFSETS = {
  full: { right: insetOffset('right', 40), bottom: insetOffset('bottom', 30) },
  compact: { right: insetOffset('right', 16), bottom: insetOffset('bottom', 8) },
};

export function footerTransportLayout({
  headerVisible,
  keepTransportVisible,
  playing,
  ended,
}: FooterTransportLayoutInput): FooterTransportLayout {
  const transportOnly = keepTransportVisible && !headerVisible;
  return {
    footerOnScreen: headerVisible || keepTransportVisible,
    transportOnly,
    showRestart: !ended && !transportOnly,
    compactPause: transportOnly && playing,
  };
}
